import React, { useEffect, useState } from 'react';
import PushPinIcon from '@mui/icons-material/PushPin';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import { Box, Snackbar } from '@mui/material';
import Accordion from './components/Accordion';
import VehicleDetailsSection from './VehicleDetailsSection';
import VehicleConditionReportSection from './VehicleConditionReportSection';
import VehicleWindowStickerSection from './VehicleWindowStickerSection';
import VehicleCertificateSection from './VehicleCertificateSection';
import IosShareIcon from '@mui/icons-material/IosShare';
import MobileNav from './components/mobileNav';
import PrintIcon from '@mui/icons-material/Print';
import Alert from '@mui/material/Alert';
import PrintModal from './components/PrintModal';
import SalesLeadDialog from './components/SalesLeadDialog';
import { CarketaDisclosure } from './components/CarketaDisclosure';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { CheckAvailabilityDialog } from 'src/@carketa/vehicle_dialog/components/views/VehicleMainView/dialogs/CheckAvailabilityDialog';
import Transportation from './components/Transportation';
import CarketaConnect from './components/CarketaConnect';
import { trpc } from 'src/utils/trpc';
import { useAnalytics } from 'use-analytics';
import { useRouterParams } from 'src/hooks/useRouterParams';
import { OrganizationPermission } from 'src/@carketa/components/control/OrganizationPermission';
import { FEATURE_FLAG } from 'src/server/common/permissions';
import { OrganizationSettings } from 'src/types/organizations/organizations';
import { defaultSalesPacketMenu } from '../organization/settings/SettingsTable';

type CarketaValuationProps = {
  vin: string;
};

function CarketaValuation({ vin }: CarketaValuationProps) {
  const { page, track } = useAnalytics();
  const { get, set, setMany } = useRouterParams();

  const [menus, setMenus] = useState<any>(defaultSalesPacketMenu);

  const inspectionId = String(get('inspectionId') || '');
  const pdfMode = Boolean(get('pdfMode') || false);

  const { data: vehicle } = trpc.vehicles.getByStickerOrId.useQuery(
    {
      vin: vin ?? '',
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: neovin } = trpc.window_sticker.getNeoVin.useQuery(
    {
      vin: vehicle ? vehicle.vin : '',
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );

  const { data: vehicle_inspections } =
    trpc.vehicle_inspection.getAllConditionReportByVehicleId.useQuery(
      { vehicle_id: vehicle ? vehicle.id : '' },
      {
        refetchOnWindowFocus: false,
        keepPreviousData: true,
      }
    );

  const organization_settings = vehicle?.organization
    ?.settings as OrganizationSettings;
  const section = String(
    get('section') ||
      organization_settings?.sales_packet_menu?.[0].section ||
      'vehicle-details'
  );

  const [showCopied, setShowCopied] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [salesLeadOpen, setSalesLeadOpen] = useState(false);

  const [checkAvailabilityModalOpen, setcheckAvailabilityModalOpen] =
    useState(false);

  const handleMouseOver = (sectionOver: string) => {
    if (section === sectionOver) return;
    set('section', sectionOver);
    track(
      'section-over',
      {
        section: sectionOver,
      },
      {
        component: 'sales-packet',
        custom: { vin },
      }
    );
  };
  const handleCloseSnackBar = () => {
    setShowCopied(false);
  };

  const openNewTabWithSelectedSection = (tabOptions: {
    inspectionId: string | null;
    targetElementId: string;
  }) => {
    const { inspectionId, targetElementId } = tabOptions;

    setMany([
      { name: 'inspectionId', value: inspectionId || undefined },
      { name: 'section', value: targetElementId || undefined },
    ]);

    track(
      'section-click',
      {
        section: targetElementId,
        inspectionId: inspectionId,
      },
      {
        component: 'sales-packet',
        custom: { vin },
      }
    );

    const targetElement = document.getElementById(targetElementId);
    if (targetElement) {
      window.scrollTo({
        top: targetElement.offsetTop - 80,
        behavior: 'smooth',
      });
    }
  };

  const sidebarMenu = (menuOptions: {
    menu: string;
    title: string;
    subTitle: string;
    id: string | null;
  }) => {
    const { menu = '', title, subTitle, id } = menuOptions;

    return (
      <div
        key={`${id}-${title}`}
        className={`border p-4 rounded mt-2 cursor-pointer ${
          inspectionId
            ? inspectionId === id
              ? 'bg-green text-white font-medium'
              : ''
            : menu === section
            ? 'bg-green text-white font-medium'
            : ''
        }`}
        onClick={() =>
          openNewTabWithSelectedSection({
            inspectionId: id,
            targetElementId: menu,
          })
        }
      >
        <div className="flex justify-start">
          <div>
            <p className="text-sm">{title}</p>
            <p className={`text-xs gray-500`}>{subTitle}</p>
          </div>
        </div>
      </div>
    );
  };

  const handleCloseCheckAvailabilityDialog = () => {
    setcheckAvailabilityModalOpen(false);
  };

  useEffect(() => {
    page({}, { component: 'sales-packet', custom: { vin } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setMenus(
      organization_settings?.sales_packet_menu || defaultSalesPacketMenu
    );
  }, [organization_settings]);

  if (!vehicle) {
    return <></>;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: '#FFF',
        color: '#000',
      }}
    >
      <Box>
        <div
          className={
            section === 'vehicle-reports' && pdfMode
              ? 'flex flex-col '
              : 'flex flex-col '
          }
        >
          <nav
            className="flex justify-between h-10 items-center flex-no-wrap fixed top-0 w-full z-10"
            style={{ backgroundColor: '#393939' }}
          >
            <Box
              sx={{
                display: pdfMode ? 'none' : 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <MobileNav>
                <Box className="flex justify-center align-center flex-col">
                  {menus.map((data: any) => {
                    if (data?.text?.toUpperCase() === 'VEHICLE DETAILS') {
                      return sidebarMenu({
                        menu: 'vehicle-details',
                        title: 'Vehicle Details',
                        subTitle: 'View vehicle information',
                        id: null,
                      });
                    }
                    if (data?.text?.toUpperCase() === 'CARKETA INSPECTION') {
                      return vehicle_inspections
                        ? vehicle_inspections.map((vehicle_inspection: any) => {
                            return sidebarMenu({
                              menu: 'vehicle-reports',
                              title:
                                vehicle_inspection.inspection_template.title,
                              subTitle: 'View vehicle inspection',
                              id: vehicle_inspection.id,
                            });
                          })
                        : null;
                    }

                    if (
                      data?.text?.toUpperCase() === 'CARKETA WINDOW STICKER'
                    ) {
                      return sidebarMenu({
                        menu: 'window-sticker',
                        title: 'Carketa Window Sticker',
                        subTitle: 'View carketa sticker',
                        id: null,
                      });
                    }

                    if (data?.text?.toUpperCase() === 'CHECKLIST CERTIFICATE') {
                      return vehicle_inspections && vehicle_inspections.length
                        ? sidebarMenu({
                            menu: 'checklist-certificate',
                            title: 'Checklist Certificate',
                            subTitle: 'View vehicle certificate',
                            id: null,
                          })
                        : null;
                    }

                    if (data?.text?.toUpperCase() === 'TRANSPORTATION') {
                      return sidebarMenu({
                        menu: 'transportation',
                        title: 'Transportation',
                        subTitle: 'View transportation status',
                        id: null,
                      });
                    }

                    if (data?.text?.toUpperCase() === 'CARKETA CONNECT') {
                      return (
                        <OrganizationPermission
                          key={'carketa_connect-mobile'}
                          vehicle_id={vehicle.id}
                          feature_flag={FEATURE_FLAG.carketa_connect}
                        >
                          {sidebarMenu({
                            menu: 'carketa-connect',
                            title: 'Carketa Connect',
                            subTitle: 'View vehicle process',
                            id: null,
                          })}
                        </OrganizationPermission>
                      );
                    }
                  })}
                </Box>
              </MobileNav>
              <img
                className=" py-2 h-10 ml-5"
                src={`https://storage.googleapis.com/carketa-next-assets/next/img/carketa-logo-white.png`}
                alt=""
              />
            </Box>
          </nav>
          <Box>
            <div className="flex flex-col items-center pb-14 pt-10">
              <div className="flex flex-1 overflow-hidden p-4 m4 gap-6 w-full justify-center ">
                <div
                  className="flex"
                  style={{
                    display: pdfMode ? 'none' : 'flex',
                  }}
                >
                  <Box
                    className={`w-[300px]`}
                    sx={{
                      display: { xs: 'none', sm: 'none', md: 'block' },
                    }}
                  >
                    <div className="flex flex-col justify-center items-center gap-6">
                      <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded">
                        <Accordion
                          title="Vehicle Documentation"
                          content={
                            <Box className="flex justify-center align-center flex-col">
                              {menus.map((data: any) => {
                                if (
                                  data?.text?.toUpperCase() ===
                                  'VEHICLE DETAILS'
                                ) {
                                  return sidebarMenu({
                                    menu: 'vehicle-details',
                                    title: 'Vehicle Details',
                                    subTitle: 'View vehicle information',
                                    id: null,
                                  });
                                }
                                if (
                                  data?.text?.toUpperCase() ===
                                  'CARKETA INSPECTION'
                                ) {
                                  return vehicle_inspections
                                    ? vehicle_inspections.map(
                                        (vehicle_inspection: any) => {
                                          return sidebarMenu({
                                            menu: 'vehicle-reports',
                                            title:
                                              vehicle_inspection
                                                .inspection_template.title,
                                            subTitle: 'View vehicle inspection',
                                            id: vehicle_inspection.id,
                                          });
                                        }
                                      )
                                    : null;
                                }

                                if (
                                  data?.text?.toUpperCase() ===
                                  'CARKETA WINDOW STICKER'
                                ) {
                                  return sidebarMenu({
                                    menu: 'window-sticker',
                                    title: 'Carketa Window Sticker',
                                    subTitle: 'View carketa sticker',
                                    id: null,
                                  });
                                }

                                if (
                                  data?.text?.toUpperCase() ===
                                  'CHECKLIST CERTIFICATE'
                                ) {
                                  return vehicle_inspections &&
                                    vehicle_inspections.length
                                    ? sidebarMenu({
                                        menu: 'checklist-certificate',
                                        title: 'Checklist Certificate',
                                        subTitle: 'View vehicle certificate',
                                        id: null,
                                      })
                                    : null;
                                }

                                if (
                                  data?.text?.toUpperCase() === 'TRANSPORTATION'
                                ) {
                                  return sidebarMenu({
                                    menu: 'transportation',
                                    title: 'Transportation',
                                    subTitle: 'View transportation status',
                                    id: null,
                                  });
                                }

                                if (
                                  data?.text?.toUpperCase() ===
                                  'CARKETA CONNECT'
                                ) {
                                  return (
                                    <OrganizationPermission
                                      key={'carketa_connect-full'}
                                      vehicle_id={vehicle.id}
                                      feature_flag={
                                        FEATURE_FLAG.carketa_connect
                                      }
                                    >
                                      {sidebarMenu({
                                        menu: 'carketa-connect',
                                        title: 'Carketa Connect',
                                        subTitle: 'View vehicle process',
                                        id: null,
                                      })}
                                    </OrganizationPermission>
                                  );
                                }
                              })}
                            </Box>
                          }
                        />
                      </div>
                      <div className="p-2 w-full outline outline-1 outline-[#bdbdbd] rounded">
                        <Accordion
                          title="Dealership Information"
                          content={
                            <div className="p-3">
                              <p className="gray-500 text-sm mb-2">
                                {vehicle?.location?.name}{' '}
                              </p>
                              <div className="flex items-center">
                                <PushPinIcon
                                  sx={{ fontSize: 'medium', mr: 1 }}
                                />
                                <p className="gray-400 text-sm">
                                  {vehicle?.location?.street1}{' '}
                                  {vehicle?.location?.city},{' '}
                                </p>
                              </div>
                              <div className="flex items-center m-1">
                                <p className="gray-400 text-sm">
                                  {vehicle?.location?.state}{' '}
                                  {vehicle?.location?.zip}
                                </p>
                              </div>
                              <div className="flex m-1 items-center">
                                <EmailIcon
                                  sx={{
                                    fontSize: 'medium',
                                    mr: 1,
                                    display: vehicle?.location?.contact_email
                                      ? ''
                                      : 'none',
                                  }}
                                />
                                <p className="gray-400 text-sm mb-1">
                                  {vehicle?.location?.contact_email}
                                </p>
                              </div>
                              <div className="flex m-1 items-center">
                                <PhoneIcon
                                  sx={{
                                    fontSize: 'medium',
                                    mr: 1,
                                    display: vehicle?.location?.contact_phone
                                      ? ''
                                      : 'none',
                                  }}
                                />
                                <p className="gray-400 text-sm mb-1">
                                  {vehicle?.location?.contact_phone}
                                </p>
                              </div>
                              <div className="flex m-1 items-center">
                                <a
                                  href={`https://${vehicle?.location?.website}`}
                                >
                                  <p className="gray-400 text-sm mb-1">
                                    https://{vehicle?.location?.website}
                                  </p>
                                </a>
                              </div>
                            </div>
                          }
                        />
                      </div>
                    </div>
                  </Box>
                </div>
                <main className="w-full xl:max-w-[1000px] lg:max-w-[600px] md:max-w-[500px] sm:max-w-[400px] xs:max-w-[400px] ">
                  {(section === 'vehicle-details' || !section) && (
                    <div
                      onMouseOver={() => handleMouseOver('vehicle-details')}
                      id="vehicle-details"
                    >
                      <VehicleDetailsSection
                        vehicle={vehicle}
                        setSalesLeadOpen={setSalesLeadOpen}
                      />
                    </div>
                  )}
                  {section === 'vehicle-reports' && (
                    <div
                      onMouseOver={() => handleMouseOver('vehicle-reports')}
                      id="vehicle-reports"
                    >
                      <VehicleConditionReportSection
                        vehicle={vehicle}
                        vehicle_inspections={vehicle_inspections}
                        inspectionId={inspectionId}
                      />
                    </div>
                  )}
                  {section === 'window-sticker' && (
                    <div
                      onMouseOver={() => handleMouseOver('window-sticker')}
                      id="window-sticker"
                    >
                      <VehicleWindowStickerSection
                        vehicle={vehicle}
                        neovin={neovin}
                      />
                    </div>
                  )}
                  {section === 'checklist-certificate' && (
                    <div
                      onMouseOver={() =>
                        handleMouseOver('checklist-certificate')
                      }
                      id="checklist-certificate"
                    >
                      <VehicleCertificateSection
                        vehicle={vehicle}
                        vehicle_inspections={vehicle_inspections}
                      />
                    </div>
                  )}
                  {section === 'transportation' && (
                    <div
                      onMouseOver={() => handleMouseOver('transportation')}
                      id="transportation"
                      className="mb-4"
                    >
                      <Transportation />
                    </div>
                  )}
                  {section === 'carketa-connect' && (
                    <div
                      onMouseOver={() => handleMouseOver('carketa-connect')}
                      id="carketa-connect"
                      className="mb-4"
                    >
                      <CarketaConnect vehicle={vehicle} />
                    </div>
                  )}
                  <div
                    style={{
                      display: pdfMode ? 'none' : '',
                    }}
                  >
                    <CarketaDisclosure />
                  </div>
                </main>
              </div>
            </div>
          </Box>
          <div
            className="flex justify-center py-2 fixed bottom-0 w-full z-10"
            style={{
              display: pdfMode ? 'none' : '',
              backgroundColor: '#393939',
            }}
          >
            <div
              className="w-1/5 flex flex-col items-center cursor-pointer"
              onClick={() => {
                track(
                  'share-click',
                  {
                    location: window.location.toString(),
                  },
                  {
                    component: 'sales-packet',
                    custom: { vin },
                  }
                );
                setShowCopied(true);
                navigator.clipboard.writeText(window.location.toString());
              }}
            >
              <IosShareIcon sx={{ color: 'white' }} />
              <p className="text-green text-xs mt-1">Share</p>
            </div>
            <OrganizationPermission
              vehicle_id={vehicle.id}
              feature_flag={FEATURE_FLAG.carketa_connect}
            >
              <Box
                className="flex flex-col items-center cursor-pointer"
                onClick={() => {
                  track(
                    'check-availability-click',
                    {},
                    {
                      component: 'sales-packet',
                      custom: { vin },
                    }
                  );
                  setcheckAvailabilityModalOpen(true);
                }}
              >
                <CheckCircleOutlineIcon sx={{ color: 'white' }} />
                <p className="text-green text-xs mt-1">Check Availability</p>
              </Box>
            </OrganizationPermission>

            <Box
              className="w-1/5 flex flex-col items-center cursor-pointer"
              onClick={() => {
                track(
                  'print-click',
                  {},
                  {
                    component: 'sales-packet',
                    custom: { vin },
                  }
                );
                setPrintModalOpen(true);
              }}
            >
              <PrintIcon sx={{ color: 'white' }} />
              <p className="text-green text-xs mt-1">Print</p>
            </Box>

            <Snackbar
              open={showCopied}
              onClose={handleCloseSnackBar}
              autoHideDuration={3000}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                variant="filled"
                elevation={3}
                onClose={handleCloseSnackBar}
                severity="success"
              >
                Copied to clipboard
              </Alert>
            </Snackbar>
          </div>
        </div>
        <PrintModal
          open={printModalOpen}
          vehicle={vehicle}
          inspections={vehicle_inspections}
          handleClose={() => {
            track(
              'print-modal-close',
              {},
              {
                component: 'sales-packet',
                custom: { vin },
              }
            );
            setPrintModalOpen(false);
          }}
        />
        <SalesLeadDialog
          open={salesLeadOpen}
          vehicle={vehicle}
          handleClose={() => setSalesLeadOpen(false)}
        />
        <CheckAvailabilityDialog
          open={checkAvailabilityModalOpen}
          handleClose={handleCloseCheckAvailabilityDialog}
          vehicle={vehicle}
        />
      </Box>
    </Box>
  );
}

export default CarketaValuation;
